import React from 'react';
import PropTypes from 'prop-types';
import detectIt from 'detect-it';
import ReactSwipe from 'react-swipe';
import ChevronRight from 'mdi-react/ChevronRightIcon';
import ChevronLeft from 'mdi-react/ChevronLeftIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import HorizontalScroll from '@prisjakt/ui-horizontal-scroll';
import { bemGenerator, noop } from '@prisjakt/ui-base';

import CarouselItem from './carousel-item';

import * as S from './styles';

import './carousel.scss';

export const SWIPE = 'swipe';
export const THUMBNAIL = 'thumbnail';
export const DOT = 'dot';

const styles = bemGenerator('pj-ui-carousel', [
  'slide',
  'indicator',
  'indicator-wrapper',
  'thumbnail',
  'thumbnail-wrapper',
]);

class Carousel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPosition: 0,
    };
  }

  componentDidMount() {
    if (this.props.focus) {
      this.carousel.focus();
    }

    if (this.props.position !== null) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({ currentPosition: this.props.position });
    }
  }

  static deriveStateFromProps = nextProps => {
    this.setState({ currentPosition: nextProps.position });
  };

  static getSwipeStyles() {
    if (!ReactSwipe.defaultProps.style.wrapper) {
      throw new Error('ReactSwipe is missing defaultProps.style.wrapper');
    }

    return {
      ...ReactSwipe.defaultProps.style,
      wrapper: { ...ReactSwipe.defaultProps.style.wrapper, display: 'flex' },
    };
  }

  getIndex(position) {
    // ReactSwipe creates more slides behind the scenes,
    // adjust index to accommodate for this.
    const children = React.Children.toArray(this.props.children).filter(
      child => child
    );
    const childCount = React.Children.count(children);

    return position % childCount;
  }

  setPosition(newPosition, type) {
    const index = this.getIndex(newPosition);

    // React swipe needs it's own emitted position to work
    // correctly if it uses more slides behind the scenes
    this.reactSwipe.slide(newPosition);

    this.props.onChange(index, type);
    this.setState({
      currentPosition: index,
    });
  }

  handleOnClick(e) {
    if (this.indicatorWrapper) {
      if (
        e.target === this.indicatorWrapper ||
        this.indicatorWrapper.contains(e.target)
      ) {
        return;
      }
    }

    this.props.onClick();
  }

  handleKeyDown(e) {
    const [ENTER, SPACE, LEFT, RIGHT] = [13, 32, 37, 39];

    switch (e.keyCode) {
      case SPACE:
        // Prevent default scroll behavior when space is hit
        e.preventDefault();
        this.props.onClick();
        break;
      case ENTER:
        this.props.onClick();
        break;
      case LEFT:
        this.reactSwipe.prev();
        break;
      case RIGHT:
        this.reactSwipe.next();
        break;
      default:
      // do nothing
    }
  }

  render() {
    const { t } = this.context;
    const {
      height,
      fallback,
      thumbnails,
      showArrows,
      showIndicator,
      transitionTime,
      clickForNext,
    } = this.props;

    // Dynamic content might be empty, use fallback
    if (!this.props.children) {
      return <div className={styles.block}>{fallback}</div>;
    }

    const children = React.Children.toArray(this.props.children).filter(
      child => child
    );

    const childCount = React.Children.count(children);
    const singleItem = childCount === 1;

    const showIndicators = !thumbnails && showIndicator && childCount > 1;
    const showThumbnails = thumbnails && childCount > 1;
    const reactSwipeStyles = Carousel.getSwipeStyles();

    reactSwipeStyles.wrapper = {
      ...reactSwipeStyles.wrapper,
      maxHeight: '100%',
    };

    return (
      <S.Carousel
        ref={div => {
          this.carousel = div;
        }}
        role="button"
        tabIndex={0}
        className={styles.block}
        onClick={e => this.handleOnClick(e)}
        onKeyDown={e => this.handleKeyDown(e)}
        style={{ minHeight: height }}
      >
        {showArrows && !singleItem && (
          <S.SwitchButton
            style={{ left: 0 }}
            size="large"
            variant="icon"
            color="secondary"
            icon={<ChevronLeft />}
            ariaLabel={t('symbols', 'symbol.show-prev')}
            onClick={() => this.reactSwipe.prev()}
            data-test="SwitchButton"
          />
        )}

        {!singleItem ? (
          <ReactSwipe
            ref={reactSwipe => {
              this.reactSwipe = reactSwipe;
            }}
            key={childCount}
            style={reactSwipeStyles}
            swipeOptions={{
              auto: 0,
              startSlide: this.state.currentPosition,
              speed: transitionTime,
              disableScroll: false,
              continuous: true,
              callback: pos => {
                this.setPosition(pos, SWIPE);
              },
            }}
          >
            {React.Children.map(children, (child, index) => (
              <div
                key={index} // eslint-disable-line react/no-array-index-key
                className={styles.slide}
                style={{ maxHeight: height }}
              >
                <CarouselItem
                  inView={index === this.state.currentPosition}
                  item={child}
                  onClick={clickForNext ? () => this.reactSwipe.next() : null}
                />
              </div>
            ))}
          </ReactSwipe>
        ) : (
          <>{this.props.children}</>
        )}

        {showArrows && !singleItem && (
          <S.SwitchButton
            style={{ right: 0 }}
            size="large"
            variant="icon"
            color="secondary"
            icon={<ChevronRight />}
            ariaLabel={t('symbols', 'symbol.show-next')}
            onClick={() => this.reactSwipe.next()}
            data-test="SwitchButton"
          />
        )}

        {showThumbnails && (
          <div className={styles['thumbnail-wrapper']}>
            <HorizontalScroll loadingHeight={80} itemWidth={90}>
              {thumbnails.map((thumbnail, index) => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <span
                  key={thumbnail}
                  role="button"
                  tabIndex={index}
                  onClick={() => this.setPosition(index, THUMBNAIL)}
                  className={styles.thumbnail.withModifier({
                    selected: index === this.state.currentPosition,
                  })}
                >
                  {typeof thumbnail === 'string' ? (
                    <img src={thumbnail} alt="" />
                  ) : (
                    thumbnail
                  )}
                </span>
              ))}
            </HorizontalScroll>
          </div>
        )}

        {showIndicators && (
          <div
            ref={div => {
              this.indicatorWrapper = div;
            }}
            className={styles['indicator-wrapper']}
          >
            {children.map((_, index) => (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                key={index} // eslint-disable-line react/no-array-index-key
                type="button"
                onClick={() => this.setPosition(index, DOT)}
                className={styles.indicator.withModifier({
                  selected: index === this.state.currentPosition,
                })}
              />
            ))}
          </div>
        )}
      </S.Carousel>
    );
  }
}

const { node, number, func, arrayOf, bool, string } = PropTypes;
Carousel.propTypes = {
  height: string.isRequired,
  focus: bool,
  showArrows: bool,
  showIndicator: bool,
  position: number,
  children: node,
  fallback: node,
  thumbnails: arrayOf(node),
  onClick: func,
  onChange: func,
  transitionTime: number,
  clickForNext: bool,
};

Carousel.defaultProps = {
  position: null,
  focus: false,
  showArrows: ['hybrid', 'mouseOnly'].includes(detectIt.deviceType),
  showIndicator: true,
  children: null,
  fallback: null,
  thumbnails: null,
  onClick: noop,
  onChange: noop,
  transitionTime: 300,
  clickForNext: false,
};

Carousel.contextType = AppContext;

export default Carousel;
